import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import LockIcon from '@material-ui/icons/Lock';
import SettingsIcon from '@material-ui/icons/Settings';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useKubernetesObjects } from '@backstage/plugin-kubernetes';
import { EmptyState } from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';

// K8s service discovery vars and system vars that are noise
const NOISE_PATTERNS = [
  /^KUBERNETES_/,
  /^DEUNA_MSA_.*_(PORT|SERVICE_HOST|SERVICE_PORT)/,
  /^HOME$/,
  /^HOSTNAME$/,
  /^PATH$/,
  /^NODE_VERSION$/,
  /^YARN_VERSION$/,
  /_PORT_\d+_TCP/,
  /_PORT=tcp:/,
];

function isNoiseVar(name: string): boolean {
  return NOISE_PATTERNS.some(pattern => pattern.test(name));
}

interface EnvVarEntry {
  name: string;
  value: string;
  source: 'direct' | 'secret' | 'configmap' | 'fieldRef' | 'resourceFieldRef';
  sourceDetail: string;
}

interface ContainerEnvVars {
  containerName: string;
  envVars: EnvVarEntry[];
}

interface ClusterEnvVars {
  clusterName: string;
  deploymentName: string;
  containers: ContainerEnvVars[];
}

// Map cluster names to friendly environment labels
function getEnvLabel(clusterName: string): string {
  const lower = clusterName.toLowerCase();
  if (lower.includes('prod') || lower.includes('pdn')) return 'Production';
  if (lower.includes('qa') || lower.includes('staging')) return 'QA';
  if (lower.includes('dev')) return 'Development';
  return clusterName;
}

// Sort environments in logical order
function getEnvOrder(label: string): number {
  if (label === 'Development') return 0;
  if (label === 'QA') return 1;
  if (label === 'Production') return 2;
  return 3;
}

function parseEnvVar(envVar: any): EnvVarEntry {
  const name = envVar.name || '';

  if (envVar.value !== undefined) {
    return { name, value: envVar.value, source: 'direct', sourceDetail: '' };
  }

  if (envVar.valueFrom) {
    if (envVar.valueFrom.secretKeyRef) {
      const ref = envVar.valueFrom.secretKeyRef;
      return {
        name,
        value: `[Secret]`,
        source: 'secret',
        sourceDetail: `${ref.name} / ${ref.key}`,
      };
    }
    if (envVar.valueFrom.configMapKeyRef) {
      const ref = envVar.valueFrom.configMapKeyRef;
      return {
        name,
        value: `[ConfigMap]`,
        source: 'configmap',
        sourceDetail: `${ref.name} / ${ref.key}`,
      };
    }
    if (envVar.valueFrom.fieldRef) {
      return {
        name,
        value: envVar.valueFrom.fieldRef.fieldPath || '[FieldRef]',
        source: 'fieldRef',
        sourceDetail: envVar.valueFrom.fieldRef.fieldPath || '',
      };
    }
    if (envVar.valueFrom.resourceFieldRef) {
      return {
        name,
        value: `[Resource: ${envVar.valueFrom.resourceFieldRef.resource || ''}]`,
        source: 'resourceFieldRef',
        sourceDetail: envVar.valueFrom.resourceFieldRef.resource || '',
      };
    }
  }

  return { name, value: '', source: 'direct', sourceDetail: '' };
}

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  searchBox: {
    minWidth: 300,
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    maxHeight: 600,
  },
  tableHeader: {
    backgroundColor: theme.palette.background.default,
  },
  varName: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    fontWeight: 500,
  },
  varValue: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    wordBreak: 'break-all',
    maxWidth: 500,
  },
  chipSecret: {
    backgroundColor: '#ff9800',
    color: '#fff',
    fontSize: '0.7rem',
    height: 22,
  },
  chipConfigMap: {
    backgroundColor: '#2196f3',
    color: '#fff',
    fontSize: '0.7rem',
    height: 22,
  },
  chipDirect: {
    backgroundColor: '#4caf50',
    color: '#fff',
    fontSize: '0.7rem',
    height: 22,
  },
  chipFieldRef: {
    backgroundColor: '#9c27b0',
    color: '#fff',
    fontSize: '0.7rem',
    height: 22,
  },
  sourceDetail: {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  statsBar: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  statChip: {
    fontSize: '0.8rem',
  },
  envTabLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
}));

export function EnvVarsRuntimeContent() {
  const classes = useStyles();
  const { entity } = useEntity();
  const { kubernetesObjects, loading, error } = useKubernetesObjects(entity);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedContainer, setSelectedContainer] = useState(0);
  const [searchFilter, setSearchFilter] = useState('');
  const [showNoise, setShowNoise] = useState(false);

  // Cache of ConfigMap data fetched via K8s proxy: "cluster/namespace/name" -> { key: value }
  const [configMapCache, setConfigMapCache] = useState<Map<string, Record<string, string>>>(new Map());
  const fetchedCmRefs = useRef(new Set<string>());

  const hasLoaded = useRef(false);

  // Collect ConfigMap refs that need fetching (not available from the K8s plugin label-based fetch)
  const pendingConfigMapRefs = useMemo(() => {
    const refs: { clusterName: string; namespace: string; cmName: string }[] = [];
    if (!kubernetesObjects?.items) return refs;

    kubernetesObjects.items.forEach((cluster: any) => {
      const clusterName = cluster.cluster.name;

      // Gather ConfigMaps already returned by the plugin (label-matched)
      const pluginCmNames = new Set<string>();
      cluster.resources?.forEach((resource: any) => {
        if (resource.type === 'configmaps') {
          resource.resources?.forEach((cm: any) => {
            if (cm.metadata?.name && cm.data) {
              pluginCmNames.add(cm.metadata.name);
            }
          });
        }
      });

      // Find ConfigMap refs in pods that are NOT in the plugin data
      cluster.resources?.forEach((resource: any) => {
        if (resource.type === 'pods') {
          resource.resources?.forEach((pod: any) => {
            const ns = pod.metadata?.namespace;
            pod.spec?.containers?.forEach((container: any) => {
              container.envFrom?.forEach((envFrom: any) => {
                if (envFrom.configMapRef) {
                  const cmName = envFrom.configMapRef.name;
                  const cacheKey = `${clusterName}/${ns}/${cmName}`;
                  if (!pluginCmNames.has(cmName) && !fetchedCmRefs.current.has(cacheKey)) {
                    refs.push({ clusterName, namespace: ns, cmName });
                  }
                }
              });
            });
          });
        }
      });
    });
    return refs;
  }, [kubernetesObjects]);

  // Fetch missing ConfigMaps via the Backstage Kubernetes proxy API
  const fetchConfigMaps = useCallback(async (
    refs: { clusterName: string; namespace: string; cmName: string }[],
  ) => {
    if (refs.length === 0) return;

    const baseUrl = await discoveryApi.getBaseUrl('kubernetes');
    const newEntries = new Map<string, Record<string, string>>();

    await Promise.all(
      refs.map(async ({ clusterName, namespace, cmName }) => {
        const cacheKey = `${clusterName}/${namespace}/${cmName}`;
        fetchedCmRefs.current.add(cacheKey);
        try {
          const response = await fetchApi.fetch(
            // ?cluster=: CloudFront no incluye la cabecera en su caché; sin esto todos los clústeres devuelven el primero.
            `${baseUrl}/proxy/api/v1/namespaces/${namespace}/configmaps/${cmName}?cluster=${encodeURIComponent(clusterName)}`,
            { headers: { 'Backstage-Kubernetes-Cluster': clusterName } },
          );
          if (response.ok) {
            const cm = await response.json();
            if (cm.data) {
              newEntries.set(cacheKey, cm.data);
            }
          }
        } catch {
          // ConfigMap not accessible — will show placeholder
        }
      }),
    );

    if (newEntries.size > 0) {
      setConfigMapCache(prev => {
        const next = new Map(prev);
        newEntries.forEach((data, key) => next.set(key, data));
        return next;
      });
    }
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    fetchConfigMaps(pendingConfigMapRefs);
  }, [pendingConfigMapRefs, fetchConfigMaps]);

  // Extract env vars from pods across all clusters, grouped by cluster with containers inside
  const clusterEnvVars: ClusterEnvVars[] = useMemo(() => {
    const clusterMap = new Map<string, ClusterEnvVars>();

    if (!kubernetesObjects?.items) return [];

    kubernetesObjects.items.forEach((cluster: any) => {
      const clusterName = cluster.cluster.name;

      // Build a map of ConfigMap name -> data for this cluster (from plugin + proxy cache)
      const configMapData = new Map<string, Record<string, string>>();

      // 1. ConfigMaps returned by the K8s plugin (label-matched)
      cluster.resources?.forEach((resource: any) => {
        if (resource.type === 'configmaps') {
          resource.resources?.forEach((cm: any) => {
            if (cm.metadata?.name && cm.data) {
              configMapData.set(cm.metadata.name, cm.data);
            }
          });
        }
      });

      // 2. ConfigMaps fetched via proxy (not label-matched, from cache)
      configMapCache.forEach((data, cacheKey) => {
        const [cName, , cmName] = cacheKey.split('/');
        if (cName === clusterName) {
          configMapData.set(cmName, data);
        }
      });

      const seenContainers = new Set<string>();

      cluster.resources?.forEach((resource: any) => {
        if (resource.type === 'pods') {
          resource.resources?.forEach((pod: any) => {
            if (!pod.metadata || !pod.spec?.containers) return;

            // Get deployment name from pod labels
            const appLabel = pod.metadata.labels?.app || pod.metadata.name?.replace(/-[a-z0-9]+-[a-z0-9]+$/, '') || 'unknown';

            pod.spec.containers.forEach((container: any) => {
              // Only take the first pod per cluster+container (all replicas have same env config)
              const containerKey = `${clusterName}/${container.name}`;
              if (seenContainers.has(containerKey)) return;
              seenContainers.add(containerKey);

              const envVars: EnvVarEntry[] = [];

              // Parse direct env vars
              if (container.env) {
                container.env.forEach((envVar: any) => {
                  envVars.push(parseEnvVar(envVar));
                });
              }

              // Parse envFrom (ConfigMaps and Secrets mounted as env)
              if (container.envFrom) {
                container.envFrom.forEach((envFrom: any) => {
                  if (envFrom.configMapRef) {
                    const cmName = envFrom.configMapRef.name;
                    const cmData = configMapData.get(cmName);
                    if (cmData) {
                      // Expand ConfigMap keys into individual env var rows
                      Object.entries(cmData).forEach(([key, value]) => {
                        envVars.push({
                          name: key,
                          value: value,
                          source: 'configmap',
                          sourceDetail: cmName,
                        });
                      });
                    } else {
                      // ConfigMap data not available yet (loading or not accessible)
                      envVars.push({
                        name: `[All from ConfigMap]`,
                        value: cmName,
                        source: 'configmap',
                        sourceDetail: cmName,
                      });
                    }
                  }
                  if (envFrom.secretRef) {
                    envVars.push({
                      name: `[All from Secret]`,
                      value: envFrom.secretRef.name,
                      source: 'secret',
                      sourceDetail: envFrom.secretRef.name,
                    });
                  }
                });
              }

              if (envVars.length > 0) {
                if (!clusterMap.has(clusterName)) {
                  clusterMap.set(clusterName, {
                    clusterName,
                    deploymentName: appLabel,
                    containers: [],
                  });
                }
                clusterMap.get(clusterName)!.containers.push({
                  containerName: container.name,
                  envVars: envVars.sort((a, b) => a.name.localeCompare(b.name)),
                });
              }
            });
          });
        }
      });
    });

    // Sort by environment order
    return Array.from(clusterMap.values()).sort(
      (a, b) => getEnvOrder(getEnvLabel(a.clusterName)) - getEnvOrder(getEnvLabel(b.clusterName)),
    );
  }, [kubernetesObjects, configMapCache]);

  if (clusterEnvVars.length > 0) {
    hasLoaded.current = true;
  }

  const kubernetesAnnotation = entity.metadata.annotations?.['backstage.io/kubernetes-id'];

  if (!kubernetesAnnotation) {
    return (
      <EmptyState
        title="No Kubernetes annotation"
        description="This entity does not have a backstage.io/kubernetes-id annotation. Add it to enable runtime env vars."
        missing="info"
      />
    );
  }

  // Only show full-page loading on the very first fetch
  if (loading && !hasLoaded.current) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
        <Typography color="textSecondary">Loading runtime environment variables...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <EmptyState
        title="Error loading Kubernetes data"
        description={`${error}`}
        missing="info"
      />
    );
  }

  if (clusterEnvVars.length === 0) {
    return (
      <EmptyState
        title="No deployments found"
        description="No Kubernetes deployments were found for this entity. The microservice may not be deployed yet."
        missing="info"
      />
    );
  }

  const currentCluster = clusterEnvVars[selectedTab] || clusterEnvVars[0];
  const currentContainer = currentCluster?.containers[selectedContainer] || currentCluster?.containers[0];
  const currentEnvVars = currentContainer?.envVars || [];

  const filteredVars = currentEnvVars.filter(v => {
    const matchesSearch = !searchFilter ||
      v.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
      v.value.toLowerCase().includes(searchFilter.toLowerCase()) ||
      v.sourceDetail.toLowerCase().includes(searchFilter.toLowerCase());
    const matchesNoise = showNoise || !isNoiseVar(v.name);
    return matchesSearch && matchesNoise;
  });

  const totalVars = currentEnvVars.length;
  const directCount = currentEnvVars.filter(v => v.source === 'direct' && !isNoiseVar(v.name)).length;
  const secretCount = currentEnvVars.filter(v => v.source === 'secret').length;
  const configMapCount = currentEnvVars.filter(v => v.source === 'configmap').length;

  return (
    <div className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <Typography variant="h6">
            Runtime Env Vars
          </Typography>
        </Box>
        <TextField
          className={classes.searchBox}
          variant="outlined"
          size="small"
          placeholder="Filter variables..."
          value={searchFilter}
          onChange={e => setSearchFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box className={classes.statsBar}>
        <Chip label={`${directCount} Direct`} className={classes.statChip} style={{ backgroundColor: '#4caf50', color: '#fff' }} size="small" />
        <Chip label={`${secretCount} Secrets`} className={classes.statChip} style={{ backgroundColor: '#ff9800', color: '#fff' }} size="small" />
        <Chip label={`${configMapCount} ConfigMaps`} className={classes.statChip} style={{ backgroundColor: '#2196f3', color: '#fff' }} size="small" />
        <Chip
          label={showNoise ? 'Hide K8s system vars' : `Show K8s system vars`}
          className={classes.statChip}
          size="small"
          variant="outlined"
          onClick={() => setShowNoise(!showNoise)}
        />
      </Box>

      <Tabs
        value={selectedTab}
        onChange={(_, newVal) => { setSelectedTab(newVal); setSelectedContainer(0); }}
        indicatorColor="primary"
        textColor="primary"
        className={classes.tabs}
      >
        {clusterEnvVars.map((cluster) => {
          // Show total non-noise vars across all containers (use first container as representative)
          const mainContainer = cluster.containers[0];
          const varCount = mainContainer?.envVars.filter(v => !isNoiseVar(v.name)).length || 0;
          return (
            <Tab
              key={cluster.clusterName}
              label={
                <Box className={classes.envTabLabel}>
                  <span>{getEnvLabel(cluster.clusterName)}</span>
                  <Chip label={varCount} size="small" variant="outlined" />
                </Box>
              }
            />
          );
        })}
      </Tabs>

      {currentCluster && (
        <Card variant="outlined">
          <CardContent style={{ padding: 0 }}>
            <Box px={2} py={1} display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <Typography variant="body2" color="textSecondary">
                  Cluster: <strong>{currentCluster.clusterName}</strong> &middot; Deployment: <strong>{currentCluster.deploymentName}</strong>
                </Typography>
                {currentCluster.containers.length > 1 && (
                  <Select
                    value={selectedContainer}
                    onChange={e => setSelectedContainer(e.target.value as number)}
                    variant="outlined"
                    style={{ height: 28, fontSize: '0.8rem', fontFamily: 'monospace' }}
                  >
                    {currentCluster.containers.map((c, idx) => (
                      <MenuItem key={c.containerName} value={idx}>
                        {c.containerName}
                      </MenuItem>
                    ))}
                  </Select>
                )}
                {currentCluster.containers.length === 1 && currentContainer && (
                  <Typography variant="body2" color="textSecondary">
                    &middot; Container: <strong>{currentContainer.containerName}</strong>
                  </Typography>
                )}
              </Box>
              <Typography variant="body2" color="textSecondary">
                {filteredVars.length} / {totalVars} vars
              </Typography>
            </Box>
            <TableContainer className={classes.tableContainer}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow className={classes.tableHeader}>
                    <TableCell style={{ fontWeight: 600, width: '30%' }}>Variable</TableCell>
                    <TableCell style={{ fontWeight: 600, width: '45%' }}>Value</TableCell>
                    <TableCell style={{ fontWeight: 600, width: '25%' }}>Source</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredVars.map((envVar, idx) => (
                    <TableRow key={`${envVar.name}-${idx}`} hover>
                      <TableCell>
                        <span className={classes.varName}>{envVar.name}</span>
                      </TableCell>
                      <TableCell>
                        <span className={classes.varValue}>{envVar.value}</span>
                      </TableCell>
                      <TableCell>
                        {envVar.source === 'secret' && (
                          <Tooltip title={envVar.sourceDetail}>
                            <Chip
                              icon={<LockIcon style={{ fontSize: 14, color: '#fff' }} />}
                              label="Secret"
                              className={classes.chipSecret}
                              size="small"
                            />
                          </Tooltip>
                        )}
                        {envVar.source === 'configmap' && (
                          <Tooltip title={envVar.sourceDetail}>
                            <Chip
                              icon={<SettingsIcon style={{ fontSize: 14, color: '#fff' }} />}
                              label="ConfigMap"
                              className={classes.chipConfigMap}
                              size="small"
                            />
                          </Tooltip>
                        )}
                        {envVar.source === 'direct' && (
                          <Chip label="Direct" className={classes.chipDirect} size="small" />
                        )}
                        {envVar.source === 'fieldRef' && (
                          <Tooltip title={envVar.sourceDetail}>
                            <Chip label="FieldRef" className={classes.chipFieldRef} size="small" />
                          </Tooltip>
                        )}
                        {envVar.source === 'resourceFieldRef' && (
                          <Tooltip title={envVar.sourceDetail}>
                            <Chip label="Resource" className={classes.chipFieldRef} size="small" />
                          </Tooltip>
                        )}
                        {envVar.sourceDetail && (
                          <Typography className={classes.sourceDetail}>
                            {envVar.sourceDetail}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredVars.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography color="textSecondary" style={{ padding: 16 }}>
                          {searchFilter ? 'No variables match the filter' : 'No environment variables found'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
